import React, { useState } from "react";
import { Button } from "primereact/button";
import { ProgressSpinner } from "primereact/progressspinner";
import "../style.css";
const AuditTrailSection = ({
  showAuditTrail,
  onToggle,
  auditTrail,
  auditTrailPagination,
  onPageChange,
  isLoading,
}) => {
  const [visibleSensitiveCells, setVisibleSensitiveCells] = useState({});

  const auditTrailColumns = Array.from(
    new Set(auditTrail.flatMap((entry) => Object.keys(entry || {}))),
  );
  const isSensitiveColumn = (column) => /auth_?key/i.test(column);
  const getSensitiveCellKey = (rowIndex, column) => `${rowIndex}-${column}`;

  const toggleSensitiveCell = (rowIndex, column) => {
    const cellKey = getSensitiveCellKey(rowIndex, column);
    setVisibleSensitiveCells((previousState) => ({
      ...previousState,
      [cellKey]: !previousState[cellKey],
    }));
  };

  const formatColumnLabel = (column) => {
    return column
      .replace(/_/g, " ")
      .replace(/\b\w/g, (letter) => letter.toUpperCase());
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return "-";
    }
  };

  const renderCellValue = (value, column, rowIndex) => {
    if (!value) return "-";

    if (!isSensitiveColumn(column)) {
      return value;
    }

    const cellKey = getSensitiveCellKey(rowIndex, column);
    const isVisible = Boolean(visibleSensitiveCells[cellKey]);

    return (
      <span className="audit-trail-sensitive-value">
        <span>{isVisible ? value : "*********************"}</span>
        <button
          type="button"
          className="audit-trail-sensitive-toggle"
          onClick={() => toggleSensitiveCell(rowIndex, column)}
          aria-label={isVisible ? "Hide auth key" : "Show auth key"}
        >
          <i className={`pi ${isVisible ? "pi-eye-slash" : "pi-eye"}`} />
        </button>
      </span>
    );
  };

  return (
    <div className="audit-trail-section">
      {/* Toggle Button */}
      <button
        type="button"
        onClick={onToggle}
        className="audit-trail-toggle"
        aria-expanded={showAuditTrail}
      >
        <i
          className={`pi ${showAuditTrail ? "pi-chevron-down" : "pi-chevron-right"}`}
        />
        <span>Audit Trail</span>
      </button>

      {showAuditTrail && (
        <div className="audit-trail-content">
          {isLoading ? (
            <div className="audit-trail-loading">
              <ProgressSpinner style={{ width: "30px", height: "30px" }} />
              <span>Loading audit trail...</span>
            </div>
          ) : auditTrail.length > 0 ? (
            <>
              <div className="audit-trail-table-wrapper">
                <table className="audit-trail-table">
                  <thead>
                    <tr>
                      {auditTrailColumns.map((column) => (
                        <th key={column}>{formatColumnLabel(column)}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {auditTrail.map((entry, index) => (
                      <tr key={index}>
                        {auditTrailColumns.map((column) => {
                          const value = entry?.[column];
                          const shouldFormatDate = /(_at|date|time)$/i.test(column);

                          return (
                            <td key={column}>
                              {shouldFormatDate
                                ? formatDate(value)
                                : renderCellValue(value, column, index)}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {auditTrailPagination.totalRecords > auditTrailPagination.pageSize && (
                <div className="audit-trail-pagination">
                  <Button
                    type="button"
                    icon="pi pi-angle-left"
                    className="p-button-text p-button-sm"
                    disabled={auditTrailPagination.page === 1}
                    onClick={() => onPageChange(auditTrailPagination.page - 1)}
                    aria-label="Previous page"
                  />
                  <span className="page-info">
                    Page {auditTrailPagination.page}
                  </span>
                  <Button
                    type="button"
                    icon="pi pi-angle-right"
                    className="p-button-text p-button-sm"
                    disabled={auditTrail.length < auditTrailPagination.pageSize}
                    onClick={() => onPageChange(auditTrailPagination.page + 1)}
                    aria-label="Next page"
                  />
                </div>
              )}
            </>
          ) : (
            <div className="audit-trail-empty">
              <i className="pi pi-info-circle" />
              <span>No audit trail records found</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AuditTrailSection;